import * as React from "react";
import { styled } from "@mui/system";
import MUITypography from "@mui/material/Typography";
import { TypographyProps as TypographyPropsMUI } from "@mui/material/Typography";

import Tooltip from "@vapor/react-material/Tooltip";

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    titleSmall: true;
    pageDescription: true;
    titleMedium: true;
    titleXSmall: true;
    titleLarge: true;
    body: true;
    captionSmall: true;
    caption: true;
    display: true;
    eyebrow: true;
    heading: true;
    label: true;
    boldLabel: true;
    labelDisabled: true;
    formLabelDisabled: true;
    filedText: true;
    leadXLarge: true;
    leadLarge: true;
    leadMedium: true;
    leadSmall: true;
    displayLarge: true;
    displayMedium: true;
    displaySmall: true;
    subtitle: true;
    body500: true;
    body700: true;
    bodySmall: true;
    bodySmall500: true;
    bodySmall700: true;
    bodyLarge: true;
    bodyLarge500: true;
    bodyLarge700: true;
    buttonLarge: true;
    button: true;
    codeInput: true;
  }
}

export type ExtendedTypographyProps = {
  component?: React.ElementType;
  /**
   * if you want to disable the tooltip for truncated text you have to make `disableTooltip` to `true`
   */
  disableTooltip?: boolean;
} & TypographyPropsMUI;

export const TruncatedTypography = (props: ExtendedTypographyProps) => {
  const containerRef = React.useRef(null);
  const [isTruncated, setIsTruncated] = React.useState(false);

  React.useEffect(() => {
    const container = containerRef.current;

    if (container) {
      setIsTruncated(container.scrollWidth > container.clientWidth);
    }
  }, [props.children]);

  return (
    <>
      <Tooltip title={isTruncated ? props.children : null} enterDelay={500}>
        <MUITypography ref={containerRef} {...props} />
      </Tooltip>
    </>
  );
};

export const BasicTypography = (props: ExtendedTypographyProps) => (
  <MUITypography {...props} />
);

export const ExtendedTypography: (
  props: ExtendedTypographyProps
) => JSX.Element = styled(
  React.forwardRef<HTMLDivElement, ExtendedTypographyProps>((props, ref) => {
    const { disableTooltip, ...others } = props;
    return (
      <>
        {!disableTooltip ? (
          <TruncatedTypography ref={ref} {...others} />
        ) : (
          <BasicTypography ref={ref} {...others} />
        )}
      </>
    );
  }),
  {
    name: "ExtendedTypography",
    slot: "Root",
    overridesResolver: (props, styles) => []
  }
)(({ theme }) => ({}));
